// src/components/settings/RoutingRulesManagement.tsx
//
// Settings → Routing Rules (Story 1.5): the org admin's per-question-type
// escalation editor. One card per question type (all five always present,
// none deletable); each rule is an ordered layer list — a layer names a
// position + attribute matchers, never a person. Editing is inline (no
// overlays): one card in edit mode at a time, add/reorder/remove layers, then
// Save the whole array. Layer identity (layer_id) is preserved through every
// edit so Story 3.4's round-robin cursors survive (AD-2). All data access goes
// through useRoutingRules → routingRulesService; idioms (silent refresh,
// focus-restore, mapped errors) follow RegionManagement (Story 1.1, hardened).

import React, { useMemo, useRef, useState } from 'react'
import { Plus, ArrowUp, ArrowDown, X } from 'lucide-react'
import { Button } from '../ui/Button'
import { useRoutingRules } from '../../hooks/useRoutingRules'
import { QUESTION_TYPES, QUESTION_TYPE_LABELS } from '../../utils/questionTypes'
import {
  newLayer,
  moveLayer,
  removeLayer,
  unmatchedPositions,
} from '../../utils/ruleLayers'
import type {
  YakRoutingRule,
  YakRuleLayer,
  YakRuleMatchAttribute,
} from '../../types/yak'

// The four attribute-type matchers offered on every layer (Decision #4: all
// four for every question type; a matcher the type never captures wildcards at
// runtime per FR-35 — config doesn't hard-block).
const MATCH_ATTRIBUTES: YakRuleMatchAttribute[] = [
  'brand',
  'region',
  'channel',
  'payer',
]

const NEW_ASSIGNMENTS_ONLY_COPY =
  'Rule changes apply to new assignments only. Open tickets keep their current assignee.'

// Sort a rule's landed rows into the fixed display order, guaranteeing one
// card per type even if the backfill/seed is momentarily incomplete.
function rulesByType(rules: YakRoutingRule[]): Array<YakRoutingRule | null> {
  const byType = new Map(rules.map(r => [r.question_type, r]))
  return QUESTION_TYPES.map(type => byType.get(type) ?? null)
}

export function RoutingRulesManagement() {
  const { rules, positionCounts, loading, error, saveLayers } =
    useRoutingRules()

  const [editingRuleId, setEditingRuleId] = useState<string | null>(null)
  const [draftLayers, setDraftLayers] = useState<YakRuleLayer[]>([])
  const [addPosition, setAddPosition] = useState('')
  const [addMatchers, setAddMatchers] = useState<YakRuleMatchAttribute[]>([])
  const [actionError, setActionError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  // Focus returns to the card's Edit trigger on Save/Cancel; falls back to the
  // section heading when that control unmounted — WCAG 2.2 AA floor
  // (RegionManagement pattern).
  const editTriggerRef = useRef<HTMLElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  const restoreFocus = (trigger: HTMLElement | null) => {
    if (trigger && document.contains(trigger)) trigger.focus()
    else headingRef.current?.focus()
  }

  const cards = useMemo(() => rulesByType(rules), [rules])

  const positionsInUse = useMemo(
    () => Object.keys(positionCounts).sort((a, b) => a.localeCompare(b)),
    [positionCounts]
  )

  const openEdit = (e: React.MouseEvent<HTMLElement>, rule: YakRoutingRule) => {
    editTriggerRef.current = e.currentTarget
    setEditingRuleId(rule.id)
    setDraftLayers(
      rule.layers.map(l => ({
        ...l,
        match_attributes: [...l.match_attributes],
      }))
    )
    setAddPosition('')
    setAddMatchers([])
    setActionError(null)
  }

  const closeEdit = () => {
    const trigger = editTriggerRef.current
    editTriggerRef.current = null
    setEditingRuleId(null)
    setDraftLayers([])
    setAddPosition('')
    setAddMatchers([])
    setActionError(null)
    restoreFocus(trigger)
  }

  const toggleMatcher = (matcher: YakRuleMatchAttribute) => {
    setAddMatchers(prev =>
      prev.includes(matcher)
        ? prev.filter(m => m !== matcher)
        : [...prev, matcher]
    )
  }

  const addLayer = () => {
    const position = addPosition.trim()
    if (!position) return
    // Keep the canonical matcher order regardless of click order.
    const ordered = MATCH_ATTRIBUTES.filter(m => addMatchers.includes(m))
    setDraftLayers(prev => [...prev, newLayer(position, ordered)])
    setAddPosition('')
    setAddMatchers([])
  }

  const reorder = (index: number, direction: 'up' | 'down') => {
    setDraftLayers(prev => moveLayer(prev, index, direction))
  }

  const dropLayer = (layerId: string) => {
    setDraftLayers(prev => removeLayer(prev, layerId))
  }

  const save = (rule: YakRoutingRule) => {
    if (pending) return
    setPending(true)
    setActionError(null)
    saveLayers(rule.id, draftLayers)
      .then(() => closeEdit())
      .catch(err =>
        setActionError(
          err instanceof Error ? err.message : 'Something went wrong'
        )
      )
      .finally(() => setPending(false))
  }

  // Read/edit-mode warning: the layers whose position has no active team
  // member (AC 5). Rendered persistently so it's seen on later visits too.
  const renderWarning = (layers: YakRuleLayer[]) => {
    const unmatched = unmatchedPositions(layers, positionCounts)
    if (unmatched.length === 0) return null
    return (
      <div
        role="note"
        className="mt-3 bg-orange-50 border border-orange-200 text-orange-800 rounded-md px-3 py-2 text-sm"
      >
        {unmatched.map(layer => {
          const index = layers.findIndex(l => l.layer_id === layer.layer_id)
          return (
            <div key={layer.layer_id}>
              <span className="font-medium">Layer {index + 1}:</span> No active
              team member has the position “{layer.position}”. Tickets reaching
              this layer will fall through.
            </div>
          )
        })}
      </div>
    )
  }

  const matcherChips = (matchers: YakRuleMatchAttribute[]) => {
    if (matchers.length === 0) {
      return (
        <span className="text-xs text-gray-400">no attribute matchers</span>
      )
    }
    return (
      <div className="flex flex-wrap gap-1">
        {matchers.map(m => (
          <span
            key={m}
            className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs"
          >
            {m}
          </span>
        ))}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Routing rules</h2>
        <div className="space-y-4" aria-hidden="true">
          {[0, 1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="h-24 bg-gray-100 rounded-lg animate-pulse"
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="md:hidden mb-4 bg-gray-50 border border-gray-200 text-gray-600 rounded-lg px-4 py-3 text-sm">
        The routing rules editor works best on a larger screen.
      </div>

      <div className="mb-4">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-xl font-bold text-gray-900 focus:outline-none"
        >
          Routing rules
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          An ordered escalation path per question type. Each layer names a
          position and the attributes that must match — never an individual.
        </p>
      </div>

      {/* Persistent, informational — not an alarm (AC 4). */}
      <div className="mb-5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg px-4 py-3 text-sm">
        {NEW_ASSIGNMENTS_ONLY_COPY}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {error}
        </div>
      )}

      <div className="space-y-4">
        {cards.map((rule, cardIndex) => {
          const type = QUESTION_TYPES[cardIndex]
          const label = QUESTION_TYPE_LABELS[type]
          const isEditing = rule != null && editingRuleId === rule.id
          const layers = isEditing ? draftLayers : (rule?.layers ?? [])

          return (
            <div key={type} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <h3 className="text-base font-semibold text-gray-900">
                  {label}
                </h3>
                {rule != null && !editingRuleId && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={e => openEdit(e, rule)}
                  >
                    Edit
                  </Button>
                )}
              </div>

              {/* Read mode */}
              {!isEditing && (
                <div className="mt-3">
                  {layers.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No layers configured — tickets of this type will be
                      assigned to the org admin.
                    </p>
                  ) : (
                    <ol className="space-y-2">
                      {layers.map((layer, i) => (
                        <li
                          key={layer.layer_id}
                          className="flex items-start gap-3 text-sm"
                        >
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs flex items-center justify-center mt-0.5">
                            {i + 1}
                          </span>
                          <div className="min-w-0">
                            <div className="text-gray-900">
                              {layer.position}
                            </div>
                            <div className="mt-1">
                              {matcherChips(layer.match_attributes)}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                  {renderWarning(layers)}
                </div>
              )}

              {/* Edit mode (rule is non-null when isEditing) */}
              {isEditing && rule != null && (
                <div className="mt-3">
                  {actionError && (
                    <div
                      role="alert"
                      className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
                    >
                      {actionError}
                    </div>
                  )}

                  {draftLayers.length === 0 ? (
                    <p className="text-sm text-gray-500 mb-3">
                      No layers yet. Tickets of this type will be assigned to
                      the org admin until you add one.
                    </p>
                  ) : (
                    <ol className="space-y-2 mb-3">
                      {draftLayers.map((layer, i) => (
                        <li
                          key={layer.layer_id}
                          className="flex items-start gap-2 border border-gray-100 rounded-md p-2"
                        >
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs flex items-center justify-center mt-0.5">
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-gray-900">
                              {layer.position}
                            </div>
                            <div className="mt-1">
                              {matcherChips(layer.match_attributes)}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              aria-label={`Move layer ${i + 1} up`}
                              className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              onClick={() => reorder(i, 'up')}
                              disabled={i === 0 || pending}
                            >
                              <ArrowUp className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Move layer ${i + 1} down`}
                              className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              onClick={() => reorder(i, 'down')}
                              disabled={i === draftLayers.length - 1 || pending}
                            >
                              <ArrowDown className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Remove layer ${i + 1}`}
                              className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              onClick={() => dropLayer(layer.layer_id)}
                              disabled={pending}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}

                  {renderWarning(draftLayers)}

                  {/* Add a layer */}
                  <div className="border-t border-gray-100 mt-3 pt-3">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Add a layer
                    </div>
                    <div className="mb-2">
                      <label
                        htmlFor="add-layer-position"
                        className="block text-xs text-gray-500 mb-1"
                      >
                        Position
                      </label>
                      <input
                        id="add-layer-position"
                        list="routing-positions"
                        placeholder="e.g. field reimbursement manager"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                        value={addPosition}
                        onChange={e => setAddPosition(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addLayer()
                          }
                          if (e.key === 'Escape') {
                            setAddPosition('')
                            setAddMatchers([])
                          }
                        }}
                      />
                      <datalist id="routing-positions">
                        {positionsInUse.map(p => (
                          <option key={p} value={p} />
                        ))}
                      </datalist>
                    </div>
                    <fieldset className="mb-3">
                      <legend className="block text-xs text-gray-500 mb-1">
                        Attributes that must match
                      </legend>
                      <div className="flex flex-wrap gap-3">
                        {MATCH_ATTRIBUTES.map(matcher => (
                          <label
                            key={matcher}
                            className="inline-flex items-center gap-1.5 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                              checked={addMatchers.includes(matcher)}
                              onChange={() => toggleMatcher(matcher)}
                            />
                            {matcher}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addLayer}
                      disabled={!addPosition.trim() || pending}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add layer
                    </Button>
                  </div>

                  {/* Save is the view's single primary action. */}
                  <div className="flex justify-end gap-2 mt-4 border-t border-gray-100 pt-4">
                    <Button
                      variant="ghost"
                      onClick={closeEdit}
                      disabled={pending}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => save(rule)}
                      loading={pending}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
